import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Waves, Facebook, Instagram, Twitter, Linkedin } from "lucide-react";
import api from "@/lib/api";

const FALLBACK_COLUMNS = [
  { label: "Customers", children: [
    { label: "Browse categories", href: "/categories" },
    { label: "Find a pro", href: "/providers" },
    { label: "Book local", href: "/providers?type=local" },
    { label: "Hire digital", href: "/providers?type=digital" },
  ]},
  { label: "Providers", children: [
    { label: "Become a provider", href: "/become-a-provider" },
    { label: "Featured pros", href: "/providers?featured=1" },
    { label: "Sign up as pro", href: "/register?intended_role=provider" },
  ]},
  { label: "Company", children: [
    { label: "About us", href: "/about" },
    { label: "Trust & safety", href: "/trust" },
    { label: "Help center", href: "/help" },
  ]},
];

export default function Footer() {
  const year = new Date().getFullYear();
  const [columns, setColumns] = useState(FALLBACK_COLUMNS);
  useEffect(() => {
    let alive = true;
    api.get("/navigation/footer").then((r) => {
      const items = r.data?.items || [];
      // Footer stores column groups as top-level items each with `children`.
      const cols = items.filter((it) => (it.children || []).length > 0);
      if (alive && cols.length > 0) setColumns(cols);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <footer className="mt-24 bg-[#071b22] text-white/80 relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 gcw-noise opacity-20" />
      {/* Grid columns: 1 brand + up to 4 admin-editable footer groups. */}
      <div className={`relative mx-auto max-w-7xl px-6 lg:px-10 py-16 grid gap-10 ${
        columns.length >= 4 ? "md:grid-cols-5"
        : columns.length === 3 ? "md:grid-cols-4"
        : columns.length === 2 ? "md:grid-cols-3"
        : "md:grid-cols-2"
      }`}>
        <div className="md:col-span-1">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/10">
              <Waves className="h-5 w-5 text-[#5296ae]" />
            </span>
            <span className="font-display font-semibold text-white text-lg">
              Gulf Coast Work
            </span>
          </div>
          <p className="mt-4 text-sm text-white/70 leading-relaxed">
            Local Services. Digital Talent. One Marketplace.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a href="#" aria-label="Twitter" className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"><Twitter className="h-4 w-4" /></a>
            <a href="#" aria-label="Instagram" className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"><Instagram className="h-4 w-4" /></a>
            <a href="#" aria-label="Facebook" className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"><Facebook className="h-4 w-4" /></a>
            <a href="#" aria-label="LinkedIn" className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"><Linkedin className="h-4 w-4" /></a>
          </div>
        </div>
        {columns.map((col, i) => (
          <FooterCol key={col.label || i} title={col.label}>
            {(col.children || []).map((child, j) => (
              <FooterLink key={child.href || j} to={child.href || "#"}>{child.label}</FooterLink>
            ))}
          </FooterCol>
        ))}
      </div>

      <div className="relative border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-white/60">
          <div>© {year} Gulf Coast Work. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <span>Serving FL · AL · MS · LA · TX</span>
            <span><Link to="/privacy" className="hover:text-white">Privacy</Link> · <Link to="/terms" className="hover:text-white">Terms</Link></span>
          </div>
        </div>
      </div>
    </footer>
  );
}

const FooterCol = ({ title, children }) => (
  <div>
    <div className="font-display text-white text-sm font-semibold mb-4 tracking-wide">{title}</div>
    <ul className="space-y-2.5">{children}</ul>
  </div>
);
const FooterLink = ({ to, children }) => (
  <li>
    <Link to={to} className="text-sm text-white/70 hover:text-white transition-colors">
      {children}
    </Link>
  </li>
);
